import React, { useEffect, useState, createRef } from 'react';
import _ from 'lodash';
import { CSVLink } from 'react-csv';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { orbitkitOrders } from 'actions/orders';
import Pagination from 'components/Pagination';
import Select from 'components/Select';
import Button from 'components/Button';
import { months, years } from 'pages/smart-print-sink/data';
import { calculateTax } from 'actions/stripe';
import OrbitkitModal from 'pages/orbitkit/OrbitkitModal';
import { getProductFromSkuAndPrintProvider } from 'selectors/products';
import {
  Container,
  Title,
  PaginationWrapper,
  OrderWrapper,
  OrderCard,
  Status,
  Header,
  Filter,
} from 'styles/pages/smart-print-sink';

const take = 20;
const salesReportRef = createRef();

const SmartSinkPrint = () => {
  const dispatch = useDispatch();
  const [orders, setOrders] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [month, setMonth] = useState('Jan');
  const [year, setYear] = useState(2022);
  const [csvFileData, setCsvFileData] = useState([]);
  const products = useSelector((state) => state?.products?.data);
  const [selectedData, setSelectedData] = useState(null);

  const fetchOrders = async () => {
    const skip = (currentPage === 0 ? currentPage : currentPage - 1) * take;
    const response = await dispatch(orbitkitOrders({ skip, take }));
    setOrders(response);
  };

  useEffect(() => {
    fetchOrders();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentPage]);

  return (
    <Container>
      <Header>
        <Title>Orbitkit</Title>

        <Filter>
          <Select
            options={months}
            value={month}
            onChange={(v) => setMonth(v)}
          />

          <Select options={years} value={year} onChange={(v) => setYear(v)} />

          <Button
            title="Sales Report"
            onClick={async () => {
              const taxInfo = await dispatch(calculateTax());
              const date = moment({ year, month: _.indexOf(months, month) });
              const payload = {
                date: `${date?.format('YYYY')}-${date?.format('MM')}`,
                status: 'Approved',
              };

              const response = await dispatch(orbitkitOrders(payload));

              const data = [
                [
                  'Order Id',
                  'Status',
                  'Print Provider',
                  'Shipping Region',
                  'Shipping Country',
                  'Tracking Number',
                  'Shipping Cost (USD)',
                  'Line Item SKU',
                  'Color',
                  'Size',
                  'Quantity',
                  'Print Type',
                  'Line Item Fulfillment Cost (USD)',
                  'Total Cost Without Tax (USD)',
                  'Tax Percentage',
                  'Tax (USD)',
                  'Total Cost (USD)',
                ],
              ];

              for (let i = 0; i < response?.orders?.length; i++) {
                const o = response?.orders[i];
                const events = o?.events;
                const shippedEvent = events?.find(
                  (e) => e?.action === 'shipped'
                );

                const orderData = o?.order_data;
                const printProvider = orderData?.print_provider;

                const p = getProductFromSkuAndPrintProvider(
                  products,
                  orderData?.items[0]?.sku,
                  printProvider
                );

                const priceInformation = p?.PrintProviders?.find(
                  (q) => q?.ProviderName === printProvider
                )?.ShippingCosts?.find(
                  (s) =>
                    s?.ShippingDestination === orderData?.address_to?.country
                );

                const totalQuantity = _.sum(
                  orderData?.items?.map((q) => q?.quantity)
                );

                let orderShippingCost =
                  totalQuantity === 1
                    ? Number(priceInformation?.PrimaryCost?.toFixed(2))
                    : Number(
                        (
                          (totalQuantity - 1) *
                            priceInformation?.SecondaryCost +
                          priceInformation?.PrimaryCost
                        )?.toFixed(2)
                      );

                for (let j = 0; j < orderData?.items?.length; j++) {
                  orderShippingCost = j > 0 ? 0 : orderShippingCost;
                  const row = [
                    orderData?.id,
                    'Approved',
                    printProvider,
                    orderData?.address_to?.region,
                    orderData?.address_to?.country,
                    `'${shippedEvent?.tracking?.trackingNumber}` || '',
                    orderShippingCost,
                  ];
                  const lineItem = orderData?.items[j];
                  const sku = lineItem?.sku;
                  const product = getProductFromSkuAndPrintProvider(
                    products,
                    sku,
                    printProvider
                  );
                  const size = lineItem?.size;
                  const color = lineItem?.color;
                  const selectedSize = product?.Sizes?.find(
                    (s) => s?.Name === size
                  );
                  const type = lineItem?.type;
                  const typePrice = selectedSize[`${type}Price`]
                    ? Number(selectedSize[`${type}Price`])
                    : 0;

                  const fulfillmentCost =
                    color === 'White'
                      ? Number(
                          (selectedSize?.WhitePrice + typePrice)?.toFixed(2)
                        )
                      : Number(
                          (selectedSize?.ColoredPrice + typePrice)?.toFixed(2)
                        );

                  const taxInfoPP = taxInfo?.find(
                    (t) => t?.PrintProvider === printProvider
                  );

                  const shippingDestinationCountry =
                    taxInfoPP?.TaxCharges?.find(
                      (s) =>
                        s?.ShipppingDestinationCountry ===
                        orderData?.address_to?.country
                    );

                  const region = shippingDestinationCountry?.TaxCharges?.find(
                    (r) => r?.ShippingState === orderData?.address_to?.region
                  );

                  const taxPercentage = region?.TaxCharge || 0;

                  const taxAmount = Number(
                    (
                      ((orderShippingCost + fulfillmentCost) * taxPercentage) /
                      100
                    ).toFixed(2)
                  );

                  row.push(sku);
                  row.push(color);
                  row.push(size);
                  row.push(lineItem?.quantity);
                  row.push(type);
                  row.push(fulfillmentCost);
                  row.push(fulfillmentCost + orderShippingCost);
                  row.push(`${taxPercentage}%`);
                  row.push(taxAmount);
                  row.push(
                    Number(
                      (
                        taxAmount +
                        orderShippingCost +
                        fulfillmentCost
                      )?.toFixed(2)
                    )
                  );
                  data.push(row);
                }
              }

              setCsvFileData(data);

              setTimeout(() => {
                salesReportRef.current?.link?.click();
              }, 0);
            }}
          />
        </Filter>
      </Header>

      {!!orders?.orders?.length && (
        <OrderWrapper>
          {orders?.orders?.map((o) => (
            <OrderCard key={o?._id} onClick={() => setSelectedData(o)}>
              <Status
                approved={o?.Status === 'Approved'}
                declined={o?.Status === 'Declined'}
                processed={o?.Status === 'Processed'}
                shipped={o?.Status === 'Shipped'}
              >
                {o?.Status}
                {o?.order_data?.order_type
                  ? `- ${o?.order_data?.order_type}`
                  : ''}
              </Status>
              <h4>Id : {o?.order_data?.id}</h4>
              <div style={{ float: 'right' }}>
                {moment(o?.events[0]?.time).format('MMM DD, YYYY')}
              </div>
            </OrderCard>
          ))}
        </OrderWrapper>
      )}

      {orders?.ordersCount > take && (
        <PaginationWrapper>
          <Pagination
            totalCount={orders?.ordersCount}
            take={take}
            page={currentPage}
            onChange={(e, v) => {
              setCurrentPage(v);
            }}
          />
        </PaginationWrapper>
      )}

      <CSVLink
        style={{ display: 'none' }}
        ref={salesReportRef}
        filename={`Orbit (Sales Report - ${month} ${year}).csv`}
        data={csvFileData}
      >
        Download me
      </CSVLink>

      {selectedData && (
        <OrbitkitModal
          open={!!selectedData}
          data={selectedData}
          onClose={() => {
            setSelectedData(false);
            fetchOrders();
          }}
        />
      )}
    </Container>
  );
};

export default SmartSinkPrint;
